import { TRAINING } from './config';
import { bestOneRm, isEffectiveSet } from './oneRm';
import { round2 } from './math';
import { num } from './text';
import type { Advice, Exercise, WorkoutSet } from './types';

export interface ExerciseSession {
  /** 'YYYY-MM-DD'. */
  date: string;
  sets: readonly WorkoutSet[];
}

/**
 * Qué significa el peso que se anota:
 * - externa: el de la barra, las mancuernas o la máquina;
 * - corporal: el LASTRE que se añade al propio peso (0 = sin lastre);
 * - asistida: la ASISTENCIA de la máquina o la banda (más kg, más fácil).
 */
type LoadKind = 'externa' | 'corporal' | 'asistida';

function loadKind(exercise: Exercise): LoadKind {
  if (exercise.assisted) return 'asistida';
  if (exercise.equipment === 'peso_corporal') return 'corporal';
  return 'externa';
}

/** La carga de la sesión en el sentido de "más es más difícil". */
function hardest(exercise: Exercise, sets: readonly WorkoutSet[]): number {
  const loads = sets.map((s) => (exercise.assisted ? -s.weightKg : s.weightKg));
  return Math.max(...loads);
}

/** Los kg anotados de la serie más difícil (para enseñarlos tal cual). */
function hardestKg(exercise: Exercise, sets: readonly WorkoutSet[]): number {
  const hard = hardest(exercise, sets);
  return exercise.assisted ? -hard : hard;
}

/**
 * Doble progresión (SPEC 7.2).
 *
 * La idea: primero se sube de repeticiones dentro del rango y solo cuando el
 * rango está lleno CON MARGEN se sube el peso. El margen es lo que evita el
 * clásico "subo peso, fallo, bajo peso" cada dos semanas.
 *
 * Devuelve como mucho un aviso, y la subida manda sobre el estancamiento:
 * cuando toca subir peso, decirle a alguien que está estancado es ruido.
 */
export function progressionAdvice(
  exercise: Exercise,
  sessions: readonly ExerciseSession[],
): Advice | null {
  const history = sessions
    .map((s) => ({
      date: s.date,
      sets: s.sets.filter((set) => set.exerciseId === exercise.id && isEffectiveSet(set)),
    }))
    .filter((s) => s.sets.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (history.length === 0) return null;

  const last = history[history.length - 1]!;

  const up = shouldIncreaseWeight(exercise, last.sets);
  if (up) return up;

  const down = shouldDeload(exercise, history);
  if (down) return down;

  return stalled(exercise, history);
}

/** Todas las series efectivas al tope del rango y con RIR >= 2. */
function shouldIncreaseWeight(exercise: Exercise, sets: readonly WorkoutSet[]): Advice | null {
  if (sets.length === 0) return null;

  const allAtTop = sets.every(
    (s) => s.reps >= exercise.repRange.max && s.rir >= TRAINING.progressRirThreshold,
  );
  if (!allAtTop) return null;

  const weight = hardestKg(exercise, sets);
  const step = exercise.incrementKg;
  const reason = `${sets.length} series de ${exercise.repRange.max} repeticiones con RIR ${Math.min(
    ...sets.map((s) => s.rir),
  )} o más${loadLabel(exercise, weight)}: te sobra margen.`;

  return { type: 'progresion', priority: 2, message: upMessage(exercise, weight, step), reason };
}

function upMessage(exercise: Exercise, weight: number, step: number): string {
  switch (loadKind(exercise)) {
    case 'asistida': {
      const next = round2(Math.max(weight - step, 0));
      return next > 0
        ? `Quita asistencia en ${exercise.name}: baja a ${num(next)} kg.`
        : `Prueba ${exercise.name} sin asistencia: te sobra margen.`;
    }
    case 'corporal':
      return weight > 0
        ? `Sube el lastre de ${exercise.name} a ${num(round2(weight + step))} kg.`
        : `Añade ${num(step)} kg de lastre a ${exercise.name}, o pasa a una variante más difícil.`;
    case 'externa':
      return `Sube ${exercise.name} a ${num(round2(weight + step))} kg.`;
  }
}

/** Dos sesiones seguidas sin llegar al mínimo del rango llegando a RIR 0. */
function shouldDeload(exercise: Exercise, history: readonly ExerciseSession[]): Advice | null {
  const needed = TRAINING.deloadFailedSessions;
  if (history.length < needed) return null;

  const recent = history.slice(-needed);
  const failedAll = recent.every((session) =>
    session.sets.some((s) => s.reps < exercise.repRange.min && s.rir === 0),
  );
  if (!failedAll) return null;

  const weight = hardestKg(
    exercise,
    recent.flatMap((s) => s.sets),
  );

  return {
    type: 'progresion',
    priority: 2,
    message: deloadMessage(exercise, weight),
    reason: `En ${needed} sesiones seguidas te has quedado por debajo de ${exercise.repRange.min} repeticiones llegando al fallo (RIR 0)${loadLabel(exercise, weight)}.`,
  };
}

function deloadMessage(exercise: Exercise, weight: number): string {
  const step = exercise.incrementKg;
  switch (loadKind(exercise)) {
    case 'asistida':
      return `Pon más asistencia en ${exercise.name}: sube a ${num(round2(weight + step))} kg, o cambia de ejercicio.`;
    case 'corporal':
      return weight > 0
        ? `Quita lastre en ${exercise.name}: baja a ${num(round2(Math.max(weight - step, 0)))} kg, o cambia de ejercicio.`
        : `En ${exercise.name} te quedas corto: pasa a una variante más fácil (asistida o negativas) o cambia de ejercicio.`;
    case 'externa': {
      const low = round2(weight * (1 - TRAINING.deloadRange.max));
      const high = round2(weight * (1 - TRAINING.deloadRange.min));
      return `Baja ${exercise.name} a ${num(low)}-${num(high)} kg, o cámbialo por otro ejercicio.`;
    }
  }
}

/**
 * El rendimiento no mejora en las últimas N sesiones (SPEC 7.2): la última
 * no supera a la primera. Se compara la última y no "alguna": un pico en
 * medio no tapa una bajada.
 *
 * Si en esa ventana se SUBIÓ la carga, no hay estancamiento: bajar de
 * repeticiones tras subir peso es el ciclo normal de la doble progresión, y el
 * propio motor lo acababa de pedir.
 */
function stalled(exercise: Exercise, history: readonly ExerciseSession[]): Advice | null {
  const needed = TRAINING.stalledSessions;
  if (history.length < needed) return null;

  const recent = history.slice(-needed);
  const loads = recent.map((s) => hardest(exercise, s.sets));
  if (loads.some((load, i) => i > 0 && load > loads[i - 1]!)) return null;

  const kind = loadKind(exercise);
  if (kind === 'externa') {
    const estimates = recent.map((s) => bestOneRm(s.sets));
    if (estimates.some((e) => e == null)) return null;
    const values = estimates as number[];
    if (values[values.length - 1]! > values[0]!) return null;

    return {
      type: 'progresion',
      priority: 3,
      message: `${exercise.name} lleva ${needed} sesiones sin avanzar. Prueba a bajar un 5-10 % y volver a subir, o a cambiar de ejercicio.`,
      reason: `1RM estimado en las últimas ${needed} sesiones: ${values.map((v) => num(v, 1)).join(' → ')} kg.`,
    };
  }

  // Con el peso corporal (o una asistencia), el 1RM depende de cuánto pesa la
  // persona. A carga constante basta comparar repeticiones; con cargas
  // distintas no se puede sin ese dato, y no se inventa.
  if (loads.some((load) => load !== loads[0])) return null;
  const reps = recent.map((s) => Math.max(...s.sets.map((set) => set.reps)));
  if (reps[reps.length - 1]! > reps[0]!) return null;

  return {
    type: 'progresion',
    priority: 3,
    message: `${exercise.name} lleva ${needed} sesiones sin avanzar. Prueba una variante algo más fácil unas semanas y vuelve, o cambia de ejercicio.`,
    reason: `Repeticiones de la mejor serie en las últimas ${needed} sesiones: ${reps.join(' → ')}.`,
  };
}

/** " a 60 kg", " con 30 kg de asistencia", " con tu peso corporal"... */
function loadLabel(exercise: Exercise, weight: number): string {
  switch (loadKind(exercise)) {
    case 'asistida':
      return ` con ${num(weight)} kg de asistencia`;
    case 'corporal':
      return weight > 0 ? ` con ${num(weight)} kg de lastre` : ' con tu peso corporal';
    case 'externa':
      return ` a ${num(weight)} kg`;
  }
}
